// ResultEntry.jsx - FIXED VERSION
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Snackbar,
  Tooltip
} from '@mui/material';
import {
  Save,
  PlayArrow,
  CheckCircle,
  Science,
  VerifiedUser,
  Print,
  ArrowBack,
  Calculate,
  Warning,
  Refresh
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { resultEntryService } from '../../../../../services/resultEntryService';
import { ReferenceRangeCalculator } from '../../../../../utils/ReferenceRangeCalculator';

export const ResultEntry = () => {
  const { id: resultId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeStep, setActiveStep] = useState(0);
  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [verificationDialog, setVerificationDialog] = useState(false);
  const [saveDialog, setSaveDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [technician, setTechnician] = useState('');
  const [instrument, setInstrument] = useState('');
  const [method, setMethod] = useState('');
  const [inputValues, setInputValues] = useState({});
  const [calculatedResults, setCalculatedResults] = useState({});

  const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;

  const steps = ['Enter Results', 'Verify', 'Release'];

  useEffect(() => {
    loadResultEntry();
  }, [resultId]);

  const loadResultEntry = async () => {
    setLoading(true);
    try {
      const response = await resultEntryService.getResultById(resultId);

      if (response.success) {
        setResultData(response.data);

        // Auto-select the service if serviceId provided in navigation state
        const { serviceId } = location.state || {};
        if (serviceId && response.data.service_results) {
          const service = response.data.service_results.find(
            sr => sr.service?._id === serviceId || sr.service === serviceId
          );
          setSelectedService(service || response.data.service_results[0]);
        } else if (response.data.service_results?.length > 0) {
          setSelectedService(response.data.service_results[0]);
        }

        // Initialize input values from loaded data
        initializeInputValues(response.data);
        updateActiveStep(response.data);

        showSnackbar('Result entry loaded successfully', 'success');
      } else {
        showSnackbar(response.message || 'Failed to load result entry', 'error');
      }
    } catch (error) {
      console.error('Error loading result entry:', error);
      showSnackbar(error.response?.data?.message || 'Failed to load result entry', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIXED: Initialize input values from result data
  // ✅ FIXED: Initialize input values with reference range calculation
  const initializeInputValues = (data) => {
    if (!data.service_results) return;

    const initialValues = {};

    // Create a deep copy to avoid mutating the original data
    const updatedServiceResults = data.service_results.map(service => {
      const updatedResults = service.results?.map(result => {
        // Store display value
        initialValues[result._id] = {
          displayValue: result.value || '',
          resultData: result
        };

        // ✅ CALCULATE REFERENCE RANGES FOR EXISTING DATA IF MISSING
        if (!result.referenceRange && result.parameter) {
          const param = service.service?.parameters?.find(p => p._id === result.parameter);
          if (param) {
            const referenceRangeInfo = getReferenceRangeForParameter(param, result);
            const valueToCheck = result.value || initialValues[result._id]?.displayValue;
            let status = 'normal';

            if (valueToCheck && !isNaN(valueToCheck)) {
              status = getResultStatusWithReference(valueToCheck, param);
            }

            // Return updated result with reference ranges
            return {
              ...result,
              referenceRange: referenceRangeInfo.displayText,
              referenceRangeText: referenceRangeInfo.displayText,
              referenceRangeMatched: referenceRangeInfo.matchType,
              appliedReferenceRange: referenceRangeInfo.matchedRange,
              status: result.status || status
            };
          }
        }

        return result;
      });

      return {
        ...service,
        results: updatedResults
      };
    });

    setInputValues(initialValues);

    // Update the state with calculated reference ranges
    setResultData(prevData => ({
      ...prevData,
      service_results: updatedServiceResults
    }));

    // Update selected service if it exists
    if (selectedService) {
      const updatedSelectedService = updatedServiceResults.find(
        s => s._id === selectedService._id
      );
      if (updatedSelectedService) {
        setSelectedService(updatedSelectedService);
      }
    }
  };

  // ✅ FIXED: Calculate derived parameters in frontend
  const calculateDerivedParameters = (service, updatedValues = {}) => {
    if (!service?.service?.parameters) return {};

    const calculations = {};
    const inputParams = service.service.parameters.filter(p => !p.isCalculated);
    const calculatedParams = service.service.parameters.filter(p => p.isCalculated);

    // Collect current values
    const currentValues = {};
    service.results?.forEach(result => {
      const param = service.service.parameters.find(p => p._id === result.parameter);
      if (param && !param.isCalculated) {
        const valueKey = result._id;
        const inputValue = updatedValues[valueKey]?.displayValue || inputValues[valueKey]?.displayValue;

        if (inputValue !== undefined && inputValue !== '') {
          currentValues[param.name] = parseFloat(inputValue) || 0;
        } else if (result.numericValue !== null) {
          currentValues[param.name] = result.numericValue;
        }
      }
    });

    // Calculate derived parameters
    calculatedParams.forEach(param => {
      if (param.formula) {
        try {
          const result = evaluateFormula(param.formula, currentValues);
          calculations[param._id] = {
            value: result.toString(),
            numericValue: result,
            unit: param.unit,
            status: 'normal'
          };
        } catch (error) {
          console.error(`Calculation failed for ${param.label}:`, error);
          calculations[param._id] = {
            value: 'Error',
            numericValue: null,
            unit: param.unit,
            status: 'abnormal'
          };
        }
      }
    });

    return calculations;
  };

  // ✅ FIXED: Formula evaluation function
  const evaluateFormula = (formula, values) => {
    // Create a safe evaluation context
    const context = {
      ...values,
      Math: Math,
      round: (value, decimals = 2) => {
        const factor = Math.pow(10, decimals);
        return Math.round(value * factor) / factor;
      },
      sum: (...args) => args.reduce((a, b) => a + b, 0),
      avg: (...args) => args.reduce((a, b) => a + b, 0) / args.length,
      max: (...args) => Math.max(...args),
      min: (...args) => Math.min(...args)
    };

    // Replace parameter names with their values
    let evaluatedFormula = formula;
    Object.keys(values).forEach(paramName => {
      evaluatedFormula = evaluatedFormula.replace(
        new RegExp(`\\b${paramName}\\b`, 'g'),
        `context.${paramName}`
      );
    });

    try {
      const result = Function('context', `return ${evaluatedFormula}`)(context);
      return Number(result.toFixed(2));
    } catch (error) {
      throw new Error(`Formula evaluation failed: ${error.message}`);
    }
  };

  // ✅ FIXED: Handle result changes with real-time calculation
  // Simplified handleResultChange - alternative approach
  // ✅ ENHANCED: Handle result changes with proper reference range calculation
  const handleResultChange = (resultId, value) => {
    if (!selectedService) return;

    // Update input values
    const updatedInputValues = {
      ...inputValues,
      [resultId]: {
        ...inputValues[resultId],
        displayValue: value
      }
    };
    setInputValues(updatedInputValues);

    // Find the parameter and result
    const changedResult = selectedService.results.find(r => r._id === resultId);
    if (!changedResult) return;

    const param = selectedService.service?.parameters?.find(
      p => p._id === changedResult.parameter
    );

    // ✅ Calculate reference range and status
    let newStatus = 'normal';
    let numericValue = null;
    let referenceRange = '';
    let referenceRangeText = '';
    let referenceRangeMatched = 'text';
    let appliedReferenceRange = null;

    if (value !== '' && !isNaN(value)) {
      numericValue = parseFloat(value);

      // Calculate reference range info
      const referenceRangeInfo = getReferenceRangeForParameter(param, changedResult);
      newStatus = getResultStatusWithReference(value, param);

      // Store reference range data
      referenceRange = referenceRangeInfo.displayText;
      referenceRangeText = referenceRangeInfo.displayText;
      referenceRangeMatched = referenceRangeInfo.matchType;
      appliedReferenceRange = referenceRangeInfo.matchedRange;
    } else if (param) {
      // Even for empty values, set reference range (but keep status as normal)
      const referenceRangeInfo = getReferenceRangeForParameter(param, changedResult);
      referenceRange = referenceRangeInfo.displayText;
      referenceRangeText = referenceRangeInfo.displayText;
      referenceRangeMatched = referenceRangeInfo.matchType;
      appliedReferenceRange = referenceRangeInfo.matchedRange;
    }

    // ✅ Update results with reference range data
    const updatedResults = selectedService.results.map(result =>
      result._id === resultId
        ? {
          ...result,
          value,
          numericValue,
          status: newStatus,
          referenceRange,
          referenceRangeText,
          referenceRangeMatched,
          appliedReferenceRange
        }
        : result
    );

    const updatedService = { ...selectedService, results: updatedResults };
    setSelectedService(updatedService);

    // Update resultData to keep them in sync
    setResultData(prevData => ({
      ...prevData,
      service_results: prevData.service_results.map(service =>
        service._id === selectedService._id ? updatedService : service
      )
    }));
  };
  // ✅ FIXED: Save results function
  // ✅ FIXED: Save results with reference ranges
  const saveResults = async () => {
    if (!selectedService || !resultData) return;
    setSaving(true);
    try {
      // Prepare results data for saving - USING CURRENT STATE WITH REFERENCE RANGES
      const resultsToSave = selectedService.results.map(result => {
        const inputValue = inputValues[result._id]?.displayValue;
        const calculatedResult = calculatedResults[result._id];

        console.log('Saving result:', {
          resultId: result._id,
          value: inputValue || result.value,
          referenceRange: result.referenceRange
        });

        return {
          _id: result._id,
          parameter: result.parameter,
          value: calculatedResult ? calculatedResult.value : (inputValue || result.value),
          numericValue: calculatedResult ? calculatedResult.numericValue :
            (inputValue && !isNaN(inputValue) ? parseFloat(inputValue) : result.numericValue),
          unit: calculatedResult ? calculatedResult.unit : result.unit,
          status: result.status, // Use current status
          remarks: result.remarks,
          // ✅ INCLUDE ALL REFERENCE RANGE FIELDS
          referenceRange: result.referenceRange || "",
          referenceRangeText: result.referenceRangeText || "",
          referenceRangeMatched: result.referenceRangeMatched || "text",
          appliedReferenceRange: result.appliedReferenceRange || null
        };
      });

      const updateData = {
        results: resultsToSave,
        status: 'testing',
        technician,
        instrument,
        method
      };

      const response = await resultEntryService.updateTestResults(
        resultData._id,
        selectedService._id,
        updateData
      );

      if (response.success) {
        setResultData(response.data);
        const updatedService = response.data.service_results.find(
          s => s._id === selectedService._id
        );
        setSelectedService(updatedService);
        initializeInputValues(response.data);
        showSnackbar('Results saved successfully', 'success');
        setSaveDialog(false);
      } else {
        showSnackbar(response.message || 'Failed to save results', 'error');
      }
    } catch (error) {
      console.error('Error saving results:', error);
      showSnackbar(error.response?.data?.message || 'Failed to save results', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ✅ FIXED: Mark as completed with backend calculation
  // ✅ FIXED: Mark as completed with reference ranges
  const markAsCompleted = async () => {
    setSaving(true);
    try {
      // First, ensure all reference ranges are calculated for current values
      const resultsWithReferenceRanges = selectedService.results.map(result => {
        const inputValue = inputValues[result._id]?.displayValue;
        const calculatedResult = calculatedResults[result._id];
        const currentValue = calculatedResult ? calculatedResult.value : (inputValue || result.value);

        // Get the parameter
        const param = selectedService.service?.parameters?.find(
          p => p._id === result.parameter
        );

        // ✅ RECALCULATE REFERENCE RANGE FOR CURRENT VALUE
        let referenceRangeInfo = {};
        let status = result.status;

        if (param && currentValue) {
          referenceRangeInfo = getReferenceRangeForParameter(param, result);
          if (!isNaN(currentValue)) {
            status = getResultStatusWithReference(currentValue, param);
          }
        }

        return {
          _id: result._id,
          parameter: result.parameter,
          value: currentValue,
          numericValue: calculatedResult ? calculatedResult.numericValue :
            (inputValue && !isNaN(inputValue) ? parseFloat(inputValue) : result.numericValue),
          unit: calculatedResult ? calculatedResult.unit : result.unit,
          status: status,
          remarks: result.remarks,
          // ✅ ENSURE REFERENCE RANGES ARE INCLUDED
          referenceRange: referenceRangeInfo.displayText || result.referenceRange || "",
          referenceRangeText: referenceRangeInfo.displayText || result.referenceRangeText || "",
          referenceRangeMatched: referenceRangeInfo.matchType || result.referenceRangeMatched || "text",
          appliedReferenceRange: referenceRangeInfo.matchedRange || result.appliedReferenceRange || null
        };
      });

      const updateData = {
        results: resultsWithReferenceRanges,
        status: 'completed',
        technician,
        instrument,
        method,
        testedAt: new Date().toISOString()
      };

      const response = await resultEntryService.updateTestResults(
        resultData._id,
        selectedService._id,
        updateData
      );

      if (response.success) {
        // Trigger backend calculation of derived parameters
        await calculateDerivedResults();

        setResultData(response.data);
        const updatedService = response.data.service_results.find(
          s => s._id === selectedService._id
        );
        setSelectedService(updatedService);
        setActiveStep(1);
        showSnackbar('Results marked as completed', 'success');
        setVerificationDialog(false);
      } else {
        showSnackbar(response.message || 'Failed to complete results', 'error');
      }
    } catch (error) {
      console.error('Error completing results:', error);
      showSnackbar(error.response?.data?.message || 'Failed to complete results', 'error');
    } finally {
      setSaving(false);
    }
  };
  // ✅ FIXED: Calculate derived results on backend
  // ✅ FIXED: Calculate derived results with reference ranges
  const calculateDerivedResults = async () => {
    try {
      const response = await resultEntryService.calculateDerivedResults(
        resultData._id,
        selectedService._id
      );

      if (response.success) {
        // After backend calculation, reinitialize to calculate reference ranges for derived parameters
        initializeInputValues(response.data);

        setResultData(response.data);
        const updatedService = response.data.service_results.find(
          s => s._id === selectedService._id
        );
        setSelectedService(updatedService);

        showSnackbar('Calculated parameters updated', 'info');
      }
    } catch (error) {
      console.error('Error calculating derived results:', error);
      showSnackbar('Error calculating derived parameters', 'error');
    }
  };

  const updateActiveStep = (data) => {
    if (!data.service_results) return;

    const selectedServiceData = selectedService || data.service_results[0];
    const status = selectedServiceData?.status;

    switch (status) {
      case 'pending':
      case 'testing':
        setActiveStep(0);
        break;
      case 'completed':
        setActiveStep(1);
        break;
      case 'verified':
      case 'released':
        setActiveStep(2);
        break;
      default:
        setActiveStep(0);
    }
  };

  const validateResult = (value, parameterId) => {
    if (value === null || value === undefined) return 'normal';
    if (value < 0) return 'critical_low';
    if (value > 1000) return 'critical_high';
    return 'normal';
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };



  // ✅ ADD THESE FUNCTIONS TO YOUR RESULTENTRY.JS FILE

  // Add this function after the calculateDerivedResults function
  const verifyResults = async () => {
    setSaving(true);
    try {
      const verificationData = {
        verifiedBy: user.SuperAdminId, // This should come from auth context
        verifiedAt: new Date().toISOString()
      };

      const response = await resultEntryService.verifyResults(
        resultData._id,
        selectedService._id,
        verificationData
      );

      if (response.success) {
        setResultData(response.data);
        const updatedService = response.data.service_results.find(
          s => s._id === selectedService._id
        );
        setSelectedService(updatedService);
        setActiveStep(2); // Move to Release step
        showSnackbar('Results verified successfully', 'success');
      } else {
        showSnackbar(response.message || 'Failed to verify results', 'error');
      }
    } catch (error) {
      console.error('Error verifying results:', error);
      showSnackbar(error.response?.data?.message || 'Failed to verify results', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Add this function after verifyResults
  const releaseResults = async () => {
    setSaving(true);
    try {
      const releaseData = {
        releasedBy: user.SuperAdminId, // This should come from auth context
        releasedAt: new Date().toISOString()
      };

      const response = await resultEntryService.releaseResults(
        resultData._id,
        releaseData
      );

      if (response.success) {
        setResultData(response.data);
        showSnackbar('Results released successfully', 'success');
        // Optionally navigate back to dashboard
        setTimeout(() => {
          navigate('/hospital/laboratory/result-dashboard');
        }, 2000);
      } else {
        showSnackbar(response.message || 'Failed to release results', 'error');
      }
    } catch (error) {
      console.error('Error releasing results:', error);
      showSnackbar(error.response?.data?.message || 'Failed to release results', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Add this function after releaseResults
  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'warning', label: 'Pending' },
      testing: { color: 'info', label: 'Testing' },
      completed: { color: 'success', label: 'Completed' },
      verified: { color: 'primary', label: 'Verified' },
      released: { color: 'secondary', label: 'Released' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="filled"
      />
    );
  };

  // Add this function after getStatusChip
  const getResultStatusChip = (status) => {
    const statusConfig = {
      normal: { color: 'success', label: 'Normal' },
      low: { color: 'warning', label: 'Low' },
      high: { color: 'error', label: 'High' },
      critical_low: { color: 'error', label: 'Critical Low' },
      critical_high: { color: 'error', label: 'Critical High' },
      abnormal: { color: 'warning', label: 'Abnormal' }
    };

    const config = statusConfig[status] || statusConfig.normal;
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
      />
    );
  };



  // Reference Range Functions
  const getReferenceRangeForParameter = (parameter, result) => {
    if (!parameter) return { displayText: 'N/A', matchType: 'text' };

    // If no reference ranges defined, use the text
    if (!parameter.referenceRanges || parameter.referenceRanges.length === 0) {
      return {
        displayText: parameter.referenceRangeText || 'N/A',
        matchType: 'text'
      };
    }

    // Get patient data
    const patientAge = resultData?.age;
    const patientGender = resultData?.gender;
    const patientAgeInDays = resultData?.age_in_days;

    if (!patientAge || !patientGender) {
      return {
        displayText: parameter.referenceRangeText || 'N/A',
        matchType: 'text'
      };
    }

    // Calculate the best reference range
    return ReferenceRangeCalculator.findBestReferenceRange(
      parameter,
      patientAge,
      patientGender,
      patientAgeInDays
    );
  };

  const getResultStatusWithReference = (value, parameter) => {
    if (!parameter || value === null || value === undefined || value === '') {
      return 'normal';
    }

    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return 'normal';

    // If no reference ranges, use basic validation
    if (!parameter.referenceRanges || parameter.referenceRanges.length === 0) {
      return validateResult(numericValue, parameter._id);
    }

    // Get patient data for reference range calculation
    const patientAge = resultData?.age;
    const patientGender = resultData?.gender;
    const patientAgeInDays = resultData?.age_in_days;

    if (!patientAge || !patientGender) {
      return validateResult(numericValue, parameter._id);
    }

    // Calculate reference range and status
    const referenceRange = ReferenceRangeCalculator.findBestReferenceRange(
      parameter,
      patientAge,
      patientGender,
      patientAgeInDays
    );

    return ReferenceRangeCalculator.getResultStatus(numericValue, referenceRange);
  };

  const renderReferenceRangeCell = (parameter, result) => {
    const referenceRangeInfo = getReferenceRangeForParameter(parameter, result);

    const matchBadge = ReferenceRangeCalculator.getMatchBadge(referenceRangeInfo.matchType);

    return (
      <TableCell>
        <Typography variant="body2" color="textSecondary">
          {referenceRangeInfo.displayText}
        </Typography>
        {referenceRangeInfo.matched && parameter.referenceRanges?.length > 0 && (
          <Tooltip title={`${matchBadge.tooltip} | Age: ${resultData?.age}, Gender: ${resultData?.gender}`}>
            <Chip
              label={matchBadge.label}
              size="small"
              color={matchBadge.color}
              variant="outlined"
              sx={{ mt: 0.5, fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>
        )}
      </TableCell>
    );
  };

  // ... Rest of the helper functions (getStatusChip, getResultStatusChip) remain the same

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" align="center" sx={{ mt: 2 }}>
          Loading result entry...
        </Typography>
      </Box>
    );
  }

  if (!resultData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load result data</Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header and patient info remains the same */}
      <Card elevation={3} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h4" gutterBottom color="primary">
                Result Entry
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Enter and verify laboratory test results
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
            >
              Back to Dashboard
            </Button>
          </Box>

          {/* Patient Info */}
          <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" color="textSecondary">Patient Name</Typography>
                <Typography variant="body1" fontWeight="medium">{resultData.patient_name}</Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography variant="subtitle2" color="textSecondary">UHID</Typography>
                <Typography variant="body1">{resultData.uhid}</Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography variant="subtitle2" color="textSecondary">Barcode</Typography>
                <Typography variant="body1" fontWeight="bold" color="primary">{resultData.barcode_no}</Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography variant="subtitle2" color="textSecondary">Age/Gender</Typography>
                <Typography variant="body1">{resultData.age} • {resultData.gender}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" color="textSecondary">Referring Doctor</Typography>
                <Typography variant="body1">{resultData.doctor?.name}</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Progress Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Test Selection Sidebar remains the same */}

        <Grid item xs={12} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Available Tests
              </Typography>
              <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                {resultData.service_results?.map((service) => (
                  <Paper
                    key={service._id}
                    elevation={selectedService?._id === service._id ? 3 : 1}
                    sx={{
                      p: 2,
                      mb: 1,
                      cursor: 'pointer',
                      backgroundColor: selectedService?._id === service._id ? 'primary.light' : 'background.paper',
                      color: selectedService?._id === service._id ? 'white' : 'text.primary',
                      '&:hover': {
                        backgroundColor: selectedService?._id === service._id ? 'primary.main' : 'grey.100'
                      }
                    }}
                    onClick={() => {
                      setSelectedService(service);
                      updateActiveStep(resultData);
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="medium">
                      {service.service?.test_name || 'Unknown Test'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      {getStatusChip(service.status)}
                      <Science fontSize="small" />
                    </Box>
                  </Paper>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Technician Info Card */}
          <Card elevation={2} sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Information
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Technician</InputLabel>
                <Select
                  value={technician}
                  label="Technician"
                  onChange={(e) => setTechnician(e.target.value)}
                >
                  <MenuItem value="tech1">Technician 1</MenuItem>
                  <MenuItem value="tech2">Technician 2</MenuItem>
                  <MenuItem value="tech3">Technician 3</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Instrument"
                value={instrument}
                onChange={(e) => setInstrument(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Method"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
              />
            </CardContent>
          </Card>

          {/* Action Buttons based on current step */}
          <Card elevation={2} sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {activeStep === 0 && (
                  <>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={() => setSaveDialog(true)}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Progress'}
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={() => setVerificationDialog(true)}
                      disabled={!selectedService || saving}
                    >
                      Mark Complete
                    </Button>
                  </>
                )}
                {activeStep === 1 && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<VerifiedUser />}
                    onClick={verifyResults}
                    disabled={saving}
                  >
                    {saving ? 'Verifying...' : 'Verify Results'}
                  </Button>
                )}
                {activeStep === 2 && (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<Print />}
                    onClick={releaseResults}
                    disabled={saving}
                  >
                    {saving ? 'Releasing...' : 'Release Results'}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={9}>
          {selectedService && (
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5">
                    {selectedService.service?.test_name || 'Unknown Test'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Status chip */}
                    <Tooltip title="Recalculate derived parameters">
                      <IconButton
                        color="primary"
                        onClick={calculateDerivedResults}
                        disabled={selectedService.status === 'released'}
                      >
                        <Refresh />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {activeStep === 0 ? (
                  // ✅ FIXED: Result Entry Form with proper input handling
                  <TableContainer component={Paper} elevation={1}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '25%' }}>Parameter</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '20%' }}>Result</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '10%' }}>Unit</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '20%' }}>Reference Range</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '15%' }}>Status</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '10%' }}>Type</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedService.results?.map((result) => {
                          const parameter = selectedService.service?.parameters?.find(
                            p => p._id === result.parameter
                          );

                          const isCalculated = parameter?.isCalculated;
                          const calculatedValue = calculatedResults[result._id];
                          const displayValue = isCalculated && calculatedValue ?
                            calculatedValue.value :
                            (inputValues[result._id]?.displayValue || result.value || '');

                          return (
                            <TableRow key={result._id} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {parameter?.label || 'Unknown Parameter'}
                                </Typography>
                                {parameter?.helpDescriptions && (
                                  <Typography variant="caption" color="textSecondary">
                                    {parameter.helpDescriptions}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  value={displayValue}
                                  onChange={(e) => handleResultChange(result._id, e.target.value)}
                                  disabled={isCalculated || selectedService.status === 'released'}
                                  placeholder={isCalculated ? "Auto-calculated" : "Enter value"}
                                  type={parameter?.inputType === 'numeric' ? 'number' : 'text'}
                                  error={result.status !== 'normal' && result.status !== undefined}
                                  inputProps={{
                                    step: "any",
                                    min: 0
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {result.unit || parameter?.unit}
                                </Typography>
                              </TableCell>
                              <TableCell>

                                {renderReferenceRangeCell(parameter, result)}

                              </TableCell>
                              <TableCell>
                                <Tooltip title={`Status: ${result.status}`}>
                                  <span>
                                    {getResultStatusChip(result.status)}
                                  </span>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                {isCalculated ? (
                                  <Chip
                                    label="Calculated"
                                    size="small"
                                    color="info"
                                    icon={<Calculate />}
                                  />
                                ) : (
                                  <Chip
                                    label="Input"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  // Read-only view for verification and release steps
                  <TableContainer component={Paper} elevation={1}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'primary.main' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Parameter</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Result</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Unit</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reference Range</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedService.results?.map((result) => {
                          const parameter = selectedService.service?.parameters?.find(
                            p => p._id === result.parameter
                          );

                          return (
                            <TableRow key={result._id} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {parameter?.label || 'Unknown Parameter'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {result.value || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {result.unit}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {renderReferenceRangeCell(parameter, result)}
                              </TableCell>
                              <TableCell>
                                <Tooltip title={`Status: ${result.status}`}>
                                  <span>
                                    {getResultStatusChip(result.status)}
                                  </span>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                {parameter?.isCalculated ? (
                                  <Chip
                                    label="Calculated"
                                    size="small"
                                    color="info"
                                    icon={<Calculate />}
                                  />
                                ) : (
                                  <Chip
                                    label="Input"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Save Confirmation Dialog */}
      <Dialog
        open={saveDialog}
        onClose={() => setSaveDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save Results</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to save the results for {selectedService?.service?.name}?
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Results will be saved as "In Progress" and can be modified later.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveResults}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Results'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verification Dialog */}
      <Dialog
        open={verificationDialog}
        onClose={() => setVerificationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedUser color="primary" />
            Complete Test Results
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to mark <strong>{selectedService?.service?.name}</strong> as completed?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Once completed, results will be sent for verification and cannot be modified.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerificationDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={markAsCompleted}
            disabled={saving}
          >
            {saving ? 'Completing...' : 'Mark as Completed'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>


    </Box>
  );
};